import { useState, useEffect, useCallback } from 'react';
import { api } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  CheckCircle2, XCircle, RefreshCw, Trash2, Eye, EyeOff,
  Send, ChevronDown, ChevronUp, Copy, ExternalLink, Bot,
  MessageSquare, Settings2, AlertTriangle, GitBranch, Edit3,
  RotateCcw, Info, Zap, BellOff
} from 'lucide-react';
import { toast } from 'sonner';

// Audience options — "mixed" removed, report_toggles handle per-GC customisation
const AUDIENCE_OPTIONS = [
  { value: 'owners',   label: 'Owners',   desc: 'Org-wide — receives alerts from ALL branches', icon: '👑' },
  { value: 'admins',   label: 'Admins',   desc: 'Org-wide — same as Owners', icon: '🛡️' },
  { value: 'managers', label: 'Managers', desc: 'Branch-level — filter by branch or receive all', icon: '👔' },
  { value: 'branch',   label: 'Branch',   desc: 'Specific branch only — must select a branch', icon: '🏪' },
];

const TOGGLE_LABELS = {
  credit_sale:               { label: 'New Credit Sale',         desc: 'Alert when a new credit is extended to a customer' },
  payment_received:          { label: 'Payment Received',        desc: 'Alert when a customer makes a payment' },
  below_capital_sale:        { label: 'Below-Capital Sale',      desc: 'Alert when a sale below capital is approved' },
  sale_voided:               { label: 'Sale Voided',             desc: 'Owner/admin visibility for invoice voids' },
  refund_processed:          { label: 'Refund Processed',        desc: 'Alert when a refund is issued' },
  charge_applied:            { label: 'Interest / Penalty',      desc: 'Auto-charge applied on an overdue invoice' },
  daily_z_report:            { label: 'Daily Z-Report',          desc: 'End-of-day summary after each close' },
  closing_reminder:          { label: 'Closing Reminder',        desc: "Nudge when a branch hasn't closed yet" },
  po_anomaly_alert:          { label: 'PO Anomaly Alert',        desc: 'New PO reorders a slow-moving product' },
  po_slow_moving_alert:      { label: 'Slow-Moving PO Alert',    desc: 'Alert when a PO includes slow-moving products' },
  slow_moving_weekly_digest: { label: 'Weekly Slow-Moving Digest', desc: 'Monday 9 AM digest of stale inventory' },
  monthly_slow_moving:       { label: 'Monthly Slow-Moving',     desc: 'Month-end rollup with PDF link' },
  low_stock_alert:           { label: 'Low Stock Alert',         desc: 'Inventory crossed reorder point' },
  branch_transfer_request:   { label: 'Branch Transfer Request', desc: 'Alert when a branch requests stock' },
  transfer_approved:         { label: 'Transfer Approved',       desc: 'Branch transfer accepted' },
  transfer_rejected:         { label: 'Transfer Rejected',       desc: 'Branch transfer rejected' },
  pin_lockout_alert:         { label: 'PIN Lockout (Security)',  desc: 'Repeated failed PIN attempts triggered a lockout' },
  backup_completed:          { label: 'Backup Completed',        desc: 'Nightly system backup finished' },
  backup_failed:             { label: 'Backup Failed',           desc: 'System backup failed — investigate' },
  test_message:              { label: 'Test Messages',           desc: 'Allow test messages (always recommended)' },
};

// Grouped sections so per-GC toggle matrix is readable instead of one
// long flat list. Each event_key MUST appear in exactly one group below.
const TOGGLE_GROUPS = [
  { id: 'sales',     title: 'Sales & POS',
    keys: ['credit_sale', 'payment_received', 'below_capital_sale',
           'sale_voided', 'refund_processed', 'charge_applied'] },
  { id: 'closing',   title: 'Daily Closing',
    keys: ['daily_z_report', 'closing_reminder'] },
  { id: 'inventory', title: 'Inventory & Purchasing',
    keys: ['po_anomaly_alert', 'po_slow_moving_alert',
           'slow_moving_weekly_digest', 'monthly_slow_moving',
           'low_stock_alert'] },
  { id: 'transfers', title: 'Branch Transfers',
    keys: ['branch_transfer_request', 'transfer_approved', 'transfer_rejected'] },
  { id: 'system',    title: 'Security & System',
    keys: ['pin_lockout_alert', 'backup_completed', 'backup_failed', 'test_message'] },
];

// ── BotFather Setup Guide ─────────────────────────────────────────────────────
function BotFatherGuide({ botUsername }) {
  const [copiedIdx, setCopiedIdx] = useState(null);
  const copy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  if (botUsername) {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-2">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
          <CheckCircle2 size={14} /> @{botUsername} is connected
        </div>
        <p className="text-xs text-emerald-600">
          Next: Add <strong>@{botUsername}</strong> to a Telegram group → give it <em>Send Messages</em> permission → type <code className="bg-emerald-100 px-1 rounded">/connect</code> in the group.
          The chat appears below within seconds.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Bot size={15} className="text-slate-500" />
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">How to create your bot (2 minutes)</p>
      </div>
      <ol className="space-y-3">
        {[
          { step: 1, text: <>Open Telegram → search <strong>@BotFather</strong> <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center gap-0.5 underline">open in Telegram <ExternalLink size={10}/></a></> },
          { step: 2, text: <>Send the command:</>, extra: '/newbot', idx: 2 },
          { step: 3, text: <>BotFather will ask for a <strong>name</strong> (e.g. "AgriBooks Alerts") then a <strong>username</strong> ending in <code className="bg-slate-200 px-1 rounded">bot</code> (e.g. <code className="bg-slate-200 px-1 rounded">AgribooksAlertsBot</code>)</> },
          { step: 4, text: <>You'll receive a token like:</>, extra: '123456789:AAF-...', idx: 4 },
          { step: 5, text: <>Paste that token in the field below and click <strong>Save & Connect</strong></> },
        ].map(({ step, text, extra, idx }) => (
          <li key={step} className="flex gap-2.5 text-xs text-slate-600">
            <span className="shrink-0 w-5 h-5 rounded-full bg-[#1A4D2E] text-white flex items-center justify-center text-[10px] font-bold mt-0.5">{step}</span>
            <div>
              <p>{text}</p>
              {extra && (
                <button onClick={() => copy(extra, idx)} className="mt-1 inline-flex items-center gap-1.5 bg-slate-200 hover:bg-slate-300 px-2 py-0.5 rounded-md font-mono text-xs transition-colors">
                  {extra}
                  {copiedIdx === idx ? <CheckCircle2 size={10} className="text-emerald-600" /> : <Copy size={10} />}
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── Map Chat Dialog ────────────────────────────────────────────────────────────
function MapChatDialog({ chat, branches, maxChats, activeCount, onSave, onClose }) {
  const [audience, setAudience] = useState(chat.audience_type || '');
  const [branchId, setBranchId] = useState(chat.branch_id || '');
  const [isActive, setIsActive] = useState(chat.is_active ?? true);
  const [toggles, setToggles] = useState({ ...(chat.report_toggles || {}) });
  const [saving, setSaving] = useState(false);

  // Show branch picker for "branch" (required) and "managers"/"admins" (optional filter)
  const showBranchPicker = audience === 'branch' || audience === 'managers' || audience === 'admins';
  const branchRequired = audience === 'branch';

  const wouldExceedLimit = isActive && !chat.is_active && activeCount >= maxChats;

  const handleSave = async () => {
    if (!audience) { toast.error('Select an audience type'); return; }
    if (branchRequired && !branchId) { toast.error('Select a branch for this audience type'); return; }
    if (wouldExceedLimit) { toast.error(`GC limit reached (${maxChats} max). Deactivate another chat first.`); return; }
    setSaving(true);
    try {
      await api.post(`/telegram/chats/${chat.id}/map`, {
        audience_type: audience,
        branch_id: branchId || '',
        is_active: isActive,
        report_toggles: toggles,
      });
      toast.success('Chat configured successfully');
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to configure chat');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-100 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">{chat.chat_title}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{chat.chat_type} · id {chat.telegram_chat_id}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">×</button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-5 space-y-5">

            {/* Audience picker */}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block font-semibold uppercase tracking-wide">Who does this GC represent?</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {AUDIENCE_OPTIONS.map(opt => (
                  <button key={opt.value}
                    data-testid={`audience-${opt.value}`}
                    onClick={() => { setAudience(opt.value); if (opt.value !== 'branch') setBranchId(''); }}
                    className={`flex items-start gap-2 p-2.5 rounded-xl border text-left transition-all ${
                      audience === opt.value ? 'border-[#1A4D2E] bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}>
                    <span className="text-base shrink-0 mt-0.5">{opt.icon}</span>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">{opt.label}</p>
                      <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Branch picker */}
            {showBranchPicker && (
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block font-semibold uppercase tracking-wide">
                  {branchRequired ? 'Branch *' : 'Branch Filter (optional)'}
                </Label>
                <select value={branchId} onChange={e => setBranchId(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20">
                  <option value="">{branchRequired ? '-- Select a branch --' : 'All branches (no filter)'}</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {!branchRequired && (
                  <p className="text-[10px] text-slate-400 mt-1">
                    Leave blank to receive alerts from all branches. Set a branch to only get alerts for that specific branch.
                  </p>
                )}
              </div>
            )}

            {/* Active toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <Label className="text-sm font-medium text-slate-700">Active</Label>
                <p className="text-[10px] text-slate-400 mt-0.5">Inactive GCs receive no notifications</p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
            {wouldExceedLimit && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                <AlertTriangle size={13} /> GC limit reached ({maxChats} max). Deactivate another chat first.
              </div>
            )}

            {/* Report toggles */}
            <div>
              <Label className="text-xs text-slate-500 mb-3 block font-semibold uppercase tracking-wide">What does this GC receive?</Label>
              <div className="space-y-1">
                {Object.entries(TOGGLE_LABELS).map(([key, { label, desc }]) => (
                  <div key={key} className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="pr-4">
                      <p className="text-xs font-medium text-slate-700">{label}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{desc}</p>
                    </div>
                    <Switch
                      checked={!!toggles[key]}
                      onCheckedChange={v => setToggles(t => ({ ...t, [key]: v }))}
                      className="shrink-0 mt-0.5"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-100 flex gap-2 justify-end shrink-0">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="save-map-btn" size="sm" onClick={handleSave} disabled={saving || !audience || (branchRequired && !branchId)}
            className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
            {saving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <CheckCircle2 size={13} className="mr-1.5" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Template Editor ────────────────────────────────────────────────────────────
function TemplateEditor({ template, onSave, onReset, onClose }) {
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);
  const isModified = body !== template.body;

  const handleSave = async () => {
    if (!body.trim()) { toast.error('Template cannot be empty'); return; }
    setSaving(true);
    try {
      await api.put(`/telegram/templates/${template.key}`, { body });
      toast.success('Template saved');
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default template?')) return;
    setSaving(true);
    try {
      await api.delete(`/telegram/templates/${template.key}`);
      toast.success('Reset to default');
      onReset();
    } catch (e) { toast.error('Failed to reset'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">{template.name}</h3>
              <p className="text-xs text-slate-400 mt-0.5">Telegram message template</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Template Body (HTML supported)</Label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={8}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 resize-none leading-relaxed"
            />
          </div>
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <p className="text-[10px] font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Available variables</p>
            <div className="flex flex-wrap gap-1">
              {(template.placeholders || []).map(p => (
                <code key={p} className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{`{${p}}`}</code>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Use <code className="bg-slate-200 px-1 rounded">&lt;b&gt;text&lt;/b&gt;</code> for bold, <code className="bg-slate-200 px-1 rounded">&lt;code&gt;text&lt;/code&gt;</code> for monospace</p>
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex items-center gap-2">
          {template.is_customized && (
            <Button variant="outline" size="sm" onClick={handleReset} disabled={saving} className="text-xs text-slate-500">
              <RotateCcw size={11} className="mr-1.5" /> Reset Default
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !isModified}
            className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
            {saving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : null}
            Save Template
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Chat Card ──────────────────────────────────────────────────────────────────
function ChatCard({ chat, branches, maxChats, activeCount, onRefresh }) {
  const [mapping, setMapping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPreviews, setShowPreviews] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(null);

  const handleSendPreview = async (reportType) => {
    setSendingPreview(reportType);
    try {
      await api.post(`/telegram/chats/${chat.id}/send-preview`, { report_type: reportType });
      toast.success(`Preview sent to Telegram — check your group`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send preview');
    }
    setSendingPreview(null);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove "${chat.chat_title}"?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/telegram/chats/${chat.id}`);
      toast.success('Chat removed');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setDeleting(false);
  };

  const audienceStyle = {
    owners:   'bg-purple-100 text-purple-700',
    admins:   'bg-blue-100 text-blue-700',
    managers: 'bg-amber-100 text-amber-700',
    branch:   'bg-teal-100 text-teal-700',
  };

  const branchName = chat.branch_id
    ? (branches.find(b => b.id === chat.branch_id)?.name || chat.branch_id.slice(0, 8))
    : null;

  // Only show preview buttons for enabled (true) toggles — except test_message
  const enabledToggles = Object.entries(chat.report_toggles || {})
    .filter(([k, v]) => v && k !== 'test_message');

  return (
    <>
      <div data-testid={`chat-card-${chat.id}`}
        className="rounded-xl border border-slate-200 bg-white overflow-hidden transition-all">
        {/* Card header row */}
        <div className="flex items-center justify-between p-3 hover:bg-slate-50/50 transition-colors">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${chat.is_active ? 'bg-emerald-100' : 'bg-slate-100'}`}>
              <MessageSquare size={14} className={chat.is_active ? 'text-emerald-600' : 'text-slate-400'} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{chat.chat_title}</p>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-[10px] text-slate-400">{chat.chat_type}</span>
                {chat.audience_type ? (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${audienceStyle[chat.audience_type] || 'bg-slate-100 text-slate-600'}`}>
                    {chat.audience_type}
                  </span>
                ) : (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Unmapped</span>
                )}
                {branchName && (
                  <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                    <GitBranch size={9} />{branchName}
                  </span>
                )}
                {enabledToggles.length > 0 && (
                  <span className="text-[10px] text-slate-400">{enabledToggles.length} alert{enabledToggles.length !== 1 ? 's' : ''} on</span>
                )}
                {!chat.is_active && <span className="text-[10px] text-slate-400 italic">inactive</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {chat.is_active && enabledToggles.length > 0 && (
              <Button size="sm" variant="outline"
                onClick={() => setShowPreviews(v => !v)}
                className={`text-xs h-7 px-2 transition-colors ${showPreviews ? 'bg-slate-100' : ''}`}
                title="Send preview messages">
                <Send size={11} className="mr-1" />
                {showPreviews ? 'Hide' : 'Preview'}
                {showPreviews ? <ChevronUp size={10} className="ml-1" /> : <ChevronDown size={10} className="ml-1" />}
              </Button>
            )}
            <Button data-testid={`map-btn-${chat.id}`} size="sm" variant="outline"
              onClick={() => setMapping(true)} className="text-xs h-7 px-2">
              <Settings2 size={11} className="mr-1" />Configure
            </Button>
            <Button data-testid={`delete-btn-${chat.id}`} size="sm" variant="outline"
              onClick={handleDelete} disabled={deleting}
              className="text-xs h-7 px-2 text-red-500 hover:text-red-600 hover:border-red-300">
              {deleting ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
            </Button>
          </div>
        </div>

        {/* Preview panel */}
        {showPreviews && (
          <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Send preview with real data
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {enabledToggles.map(([key]) => {
                const label = (TOGGLE_LABELS[key] || {}).label || key;
                const isSending = sendingPreview === key;
                return (
                  <button key={key}
                    data-testid={`preview-${key}-${chat.id}`}
                    onClick={() => handleSendPreview(key)}
                    disabled={!!sendingPreview}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#1A4D2E] hover:bg-emerald-50 transition-all text-left text-xs disabled:opacity-50 disabled:cursor-wait group">
                    {isSending
                      ? <RefreshCw size={11} className="shrink-0 animate-spin text-[#1A4D2E]" />
                      : <Send size={11} className="shrink-0 text-slate-400 group-hover:text-[#1A4D2E] transition-colors" />
                    }
                    <span className="truncate text-slate-600 group-hover:text-[#1A4D2E]">{label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Uses latest real data from your org. Falls back to sample data if no transactions yet.
            </p>
          </div>
        )}
      </div>

      {mapping && (
        <MapChatDialog
          chat={chat} branches={branches}
          maxChats={maxChats} activeCount={activeCount}
          onSave={() => { setMapping(false); onRefresh(); }}
          onClose={() => setMapping(false)}
        />
      )}
    </>
  );
}

// ── Main TelegramSettingsTab ──────────────────────────────────────────────────
export function TelegramSettingsTab({ branches = [] }) {
  const [bot, setBot] = useState(null);
  const [chats, setChats] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenInput, setTokenInput] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // Branch-mute state: list of {branch_id, branch_name, branch_code, telegram_muted}
  const [muteRows, setMuteRows] = useState([]);
  const [muteLoading, setMuteLoading] = useState(false);
  const [muteFlipping, setMuteFlipping] = useState(null); // branch_id currently being toggled

  const fetchMuteStatus = useCallback(async () => {
    setMuteLoading(true);
    try {
      const res = await api.get('/telegram/branch-mute');
      setMuteRows(res.data.branches || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load branch mute status');
    } finally {
      setMuteLoading(false);
    }
  }, []);

  const handleToggleMute = async (row) => {
    const next = !row.telegram_muted;
    setMuteFlipping(row.branch_id);
    try {
      const res = await api.post(`/telegram/branch/${row.branch_id}/mute`, { disabled: next });
      toast.success(
        next
          ? `🔕 ${row.branch_name} muted. Purged ${res.data.purged_in_flight || 0} pending message(s).`
          : `🔔 ${row.branch_name} unmuted — alerts will resume.`
      );
      setMuteRows(prev => prev.map(r =>
        r.branch_id === row.branch_id ? { ...r, telegram_muted: next } : r
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to flip mute');
    } finally {
      setMuteFlipping(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [botRes, chatsRes, tmplRes] = await Promise.all([
        api.get('/telegram/bots').catch(() => ({ data: { bot: null } })),
        api.get('/telegram/chats').catch(() => ({ data: { chats: [] } })),
        api.get('/telegram/templates').catch(() => ({ data: { templates: [] } })),
      ]);
      setBot(botRes.data.bot);
      setChats(chatsRes.data.chats || []);
      setTemplates(tmplRes.data.templates || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await api.get('/telegram/queue?limit=50');
      setQueue(res.data.queue || []);
    } catch (e) { toast.error('Failed to load queue'); }
    setQueueLoading(false);
  };

  const handleSaveBot = async () => {
    if (!tokenInput.trim()) { toast.error('Paste the token from BotFather first'); return; }
    if (!tokenInput.includes(':')) { toast.error('Invalid token format — should look like 123456789:AAF...'); return; }
    setSaving(true);
    try {
      const res = await api.post('/telegram/bots', { bot_token: tokenInput.trim() });
      toast.success(`✅ @${res.data.bot_username} connected! Webhook is live.`);
      setTokenInput('');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to connect bot');
    }
    setSaving(false);
  };

  const handleDisconnect = async () => {
    if (!bot || !window.confirm('Disconnect this bot? All chats will be deactivated.')) return;
    setDisconnecting(true);
    try {
      await api.delete(`/telegram/bots/${bot.id}`);
      toast.success('Bot disconnected');
      setBot(null); setChats([]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to disconnect'); }
    setDisconnecting(false);
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 py-8"><RefreshCw size={14} className="animate-spin" /> Loading…</div>;
  }

  const activeChats = chats.filter(c => c.is_active);
  const unmapped = chats.filter(c => !c.audience_type);
  // Dynamic limit: 2 org-wide + 1 per branch (min 3)
  const maxChats = Math.max(3, 2 + branches.length);

  return (
    <div className="space-y-5" data-testid="telegram-settings-tab">
      <Tabs defaultValue="setup">
        <TabsList className="mb-2">
          <TabsTrigger value="setup" className="flex items-center gap-1.5 text-xs">
            <Bot size={13} /> Bot Setup
          </TabsTrigger>
          {bot && (
            <TabsTrigger value="chats" className="flex items-center gap-1.5 text-xs">
              <MessageSquare size={13} /> Group Chats
              {unmapped.length > 0 && <span className="ml-1 bg-amber-400 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{unmapped.length}</span>}
            </TabsTrigger>
          )}
          {bot && (
            <TabsTrigger value="templates" className="flex items-center gap-1.5 text-xs">
              <Edit3 size={13} /> Templates
            </TabsTrigger>
          )}
          {bot && (
            <TabsTrigger value="queue" className="flex items-center gap-1.5 text-xs">
              <Zap size={13} /> Queue Log
            </TabsTrigger>
          )}
          {bot && (
            <TabsTrigger
              data-testid="telegram-branch-mute-tab"
              value="mute"
              onClick={() => { if (muteRows.length === 0) fetchMuteStatus(); }}
              className="flex items-center gap-1.5 text-xs"
            >
              <BellOff size={13} /> Branch Mute
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Setup Tab ───────────────────────────────────────────────────── */}
        <TabsContent value="setup">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Bot size={16} className="text-[#1A4D2E]" /> Telegram Bot
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {bot ? (
                <div className="space-y-4">
                  {/* Connected status */}
                  <div className="flex items-center justify-between p-3.5 rounded-xl bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                        <Bot size={18} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-800">@{bot.bot_username}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {bot.webhook_registered ? (
                            <span className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                              <CheckCircle2 size={11} /> Webhook active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[11px] text-amber-600 font-medium">
                              <AlertTriangle size={11} /> Webhook not registered
                            </span>
                          )}
                          <span className="text-[11px] text-emerald-600">
                            · {activeChats.length}/{maxChats} GCs used
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button data-testid="disconnect-bot-btn" size="sm" variant="outline"
                      onClick={handleDisconnect} disabled={disconnecting}
                      className="text-xs border-red-200 text-red-600 hover:bg-red-50">
                      {disconnecting ? <RefreshCw size={11} className="animate-spin mr-1" /> : <XCircle size={11} className="mr-1" />}
                      Disconnect
                    </Button>
                  </div>

                  <BotFatherGuide botUsername={bot.bot_username} />

                  {/* GC limit info */}
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200 text-xs text-blue-700">
                    <Info size={13} className="shrink-0 mt-0.5" />
                    <span>
                      <strong>GC limit: {maxChats}</strong> ({branches.length} branch{branches.length !== 1 ? 'es' : ''} × 1 + 2 org-wide slots).
                      Add more branches to increase the limit. This keeps notifications organized and manageable.
                    </span>
                  </div>

                  {/* Rotate token */}
                  <details className="group">
                    <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600 select-none list-none flex items-center gap-1">
                      <ChevronDown size={12} className="group-open:rotate-180 transition-transform" /> Update / rotate bot token
                    </summary>
                    <div className="mt-2 flex gap-2">
                      <div className="relative flex-1">
                        <Input data-testid="bot-token-input" type={showToken ? 'text' : 'password'}
                          value={tokenInput} onChange={e => setTokenInput(e.target.value)}
                          placeholder="Paste new token from BotFather…" className="pr-9 font-mono text-xs" />
                        <button type="button" onClick={() => setShowToken(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                          {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                      <Button data-testid="save-bot-btn" onClick={handleSaveBot} disabled={saving}
                        className="bg-[#1A4D2E] hover:bg-[#14532d] text-white shrink-0">
                        {saving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : null}Update
                      </Button>
                    </div>
                  </details>
                </div>
              ) : (
                <div className="space-y-4">
                  <BotFatherGuide botUsername={null} />
                  <div>
                    <Label className="text-xs text-slate-500 mb-1.5 block font-medium">Bot Token from BotFather</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input data-testid="bot-token-input" type={showToken ? 'text' : 'password'}
                          value={tokenInput} onChange={e => setTokenInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSaveBot()}
                          placeholder="123456789:AAF..." className="pr-9 font-mono text-sm" />
                        <button type="button" onClick={() => setShowToken(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                          {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                      <Button data-testid="save-bot-btn" onClick={handleSaveBot}
                        disabled={saving || !tokenInput.trim()}
                        className="bg-[#1A4D2E] hover:bg-[#14532d] text-white shrink-0">
                        {saving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <CheckCircle2 size={13} className="mr-1.5" />}
                        Save & Connect
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Group Chats Tab ──────────────────────────────────────────────── */}
        {bot && (
          <TabsContent value="chats">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <MessageSquare size={16} className="text-[#1A4D2E]" /> Group Chats
                    <Badge variant="secondary" className="text-xs">{activeChats.length}/{maxChats} active</Badge>
                  </CardTitle>
                  <Button data-testid="refresh-chats-btn" variant="outline" size="sm" onClick={fetchData} className="text-xs h-7">
                    <RefreshCw size={11} className="mr-1.5" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {chats.length === 0 ? (
                  <div className="text-center py-10 space-y-3">
                    <MessageSquare size={36} className="mx-auto text-slate-200" />
                    <div>
                      <p className="text-sm font-medium text-slate-500">No group chats detected yet</p>
                      <p className="text-xs text-slate-400 mt-1">Add <strong>@{bot.bot_username}</strong> to a Telegram group → type <code className="bg-slate-100 px-1 rounded">/connect</code></p>
                    </div>
                  </div>
                ) : (
                  <>
                    {unmapped.length > 0 && (
                      <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700">
                        <AlertTriangle size={13} className="shrink-0" />
                        {unmapped.length} chat{unmapped.length > 1 ? 's' : ''} detected but not yet configured — click <strong>Configure</strong> to set audience and alerts.
                      </div>
                    )}
                    <div className="space-y-2">
                      {chats.map(chat => (
                        <ChatCard key={chat.id} chat={chat} branches={branches}
                          maxChats={maxChats} activeCount={activeChats.length}
                          onRefresh={fetchData} />
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── Templates Tab ───────────────────────────────────────────────── */}
        {bot && (
          <TabsContent value="templates">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Edit3 size={16} className="text-[#1A4D2E]" /> Message Templates
                </CardTitle>
                <p className="text-xs text-slate-400 mt-1">Customise the text sent to your group chats. HTML formatting supported.</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {templates.map(t => (
                    <div key={t.key}
                      className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-slate-300 transition-all group">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-700">{t.name}</p>
                          {t.is_customized && (
                            <Badge variant="secondary" className="text-[10px] bg-blue-100 text-blue-700">Custom</Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 truncate max-w-md">{t.body?.slice(0, 80).replace(/<[^>]+>/g, '')}…</p>
                      </div>
                      <Button data-testid={`edit-template-${t.key}`}
                        size="sm" variant="outline" onClick={() => setEditingTemplate(t)}
                        className="ml-3 text-xs h-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Edit3 size={11} className="mr-1" /> Edit
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── Queue Log Tab ────────────────────────────────────────────────── */}
        {bot && (
          <TabsContent value="queue">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Zap size={16} className="text-[#1A4D2E]" /> Message Queue Log
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchQueue} disabled={queueLoading} className="text-xs h-7">
                    {queueLoading ? <RefreshCw size={11} className="animate-spin mr-1" /> : <RefreshCw size={11} className="mr-1" />} Load
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Messages are sent every 30 seconds. Up to 3 retries on failure.</p>
              </CardHeader>
              <CardContent>
                {queue.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    <Zap size={32} className="mx-auto opacity-20 mb-2" />
                    Click <strong>Load</strong> to fetch the latest queue entries.
                  </div>
                ) : (
                  <ScrollArea className="h-72">
                    <div className="space-y-1.5">
                      {queue.map(row => (
                        <div key={row.id} data-testid={`queue-${row.id}`}
                          className="flex items-start gap-2 p-2.5 rounded-lg bg-slate-50 text-xs">
                          <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            row.status === 'sent'    ? 'bg-emerald-100 text-emerald-700' :
                            row.status === 'failed'  ? 'bg-red-100 text-red-700' :
                            row.status === 'sending' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-200 text-slate-600'
                          }`}>{row.status}</span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-600 truncate">{row.report_type}</p>
                            <p className="text-slate-400 truncate">{(row.message || '').replace(/<[^>]+>/g, '').slice(0, 80)}</p>
                            {row.error && <p className="text-red-500 mt-0.5 truncate">↳ {row.error}</p>}
                            <p className="text-slate-300 mt-0.5">{(row.created_at || '').slice(0, 19).replace('T', ' ')}</p>
                          </div>
                          {row.attempts > 0 && <span className="shrink-0 text-slate-400">{row.attempts}×</span>}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── Branch Mute Tab ──────────────────────────────────────────────── */}
        {bot && (
          <TabsContent value="mute">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <BellOff size={16} className="text-[#1A4D2E]" /> Mute Telegram Per Branch
                  </CardTitle>
                  <Button
                    data-testid="refresh-mute-status-btn"
                    variant="outline" size="sm"
                    onClick={fetchMuteStatus}
                    disabled={muteLoading}
                    className="text-xs h-7"
                  >
                    {muteLoading
                      ? <RefreshCw size={11} className="animate-spin mr-1.5" />
                      : <RefreshCw size={11} className="mr-1.5" />}
                    Refresh
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Turning a branch off stops <strong>all</strong> Telegram alerts for that
                  branch — Z-Reports, credit sales, payments, transfers, below-capital,
                  closing reminders, etc. Already-queued messages are cancelled on the spot.
                  Org-wide alerts (no specific branch) continue normally.
                </p>
              </CardHeader>
              <CardContent>
                {muteLoading && muteRows.length === 0 ? (
                  <div className="flex items-center gap-2 text-slate-400 py-6 justify-center text-sm">
                    <RefreshCw size={13} className="animate-spin" /> Loading branches…
                  </div>
                ) : muteRows.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    <BellOff size={32} className="mx-auto opacity-20 mb-2" />
                    No active branches found in this organization.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {muteRows.map(row => (
                      <div
                        key={row.branch_id}
                        data-testid={`branch-mute-row-${row.branch_id}`}
                        className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                          row.telegram_muted
                            ? 'border-red-200 bg-red-50/40'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-slate-700">
                              {row.branch_name}
                            </p>
                            {row.branch_code && (
                              <Badge variant="secondary" className="text-[10px] font-normal">
                                {row.branch_code}
                              </Badge>
                            )}
                            {row.telegram_muted && (
                              <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">
                                <BellOff size={9} className="mr-1" /> Muted
                              </Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {row.telegram_muted
                              ? 'No Telegram alerts will be sent for this branch.'
                              : 'Receiving alerts normally.'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          <Switch
                            data-testid={`branch-mute-switch-${row.branch_id}`}
                            checked={!row.telegram_muted}
                            disabled={muteFlipping === row.branch_id}
                            onCheckedChange={() => handleToggleMute(row)}
                          />
                          <span className="text-[10px] text-slate-400 w-10 text-right">
                            {muteFlipping === row.branch_id
                              ? '…'
                              : (row.telegram_muted ? 'OFF' : 'ON')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Template editor modal */}
      {editingTemplate && (
        <TemplateEditor
          template={editingTemplate}
          onSave={() => { setEditingTemplate(null); fetchData(); }}
          onReset={() => { setEditingTemplate(null); fetchData(); }}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </div>
  );
}
